/**
 * La huella, en los ajustes.
 *
 * ---------------------------------------------------------------------------
 *  Lo que se dice aquí, con estas palabras
 * ---------------------------------------------------------------------------
 *
 * Se dice **qué se gana** (no escribir la contraseña en este dispositivo) y **qué no
 * cambia** (la contraseña sigue existiendo y sigue sirviendo). Y se dice también dónde
 * está la seguridad: lo que se guarda en el servidor es la clave **pública**, que no
 * permite entrar a nadie.
 *
 * Una pantalla que dijera solo «activa el acceso con huella» estaría vendiendo una
 * comodidad como si fuera una protección, y el usuario no sabría si su contraseña sigue
 * valiendo ni qué pasa si pierde el teléfono.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fingerprint, ShieldCheck, Smartphone, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { deleteWebAuthnCredential, getWebAuthnCredentials } from '../lib/endpoints';
import {
  describeBiometricError,
  enrollBiometrics,
  hasPlatformAuthenticator,
} from '../lib/biometrics';
import { Note } from './ui';

export function BiometricSettings(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [supported, setSupported] = useState<boolean | null>(null);
  const [label, setLabel] = useState('Este dispositivo');
  const [enrolling, setEnrolling] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const credentials = useQuery({
    queryKey: ['webauthn', 'credentials'],
    queryFn: getWebAuthnCredentials,
  });

  // Se pregunta al dispositivo si puede, en lugar de suponerlo. Un computador sin lector
  // de huellas soporta WebAuthn y no puede usarlo.
  useEffect(() => {
    void hasPlatformAuthenticator().then(setSupported);
  }, []);

  const enroll = async (): Promise<void> => {
    setEnrolling(true);
    setMessage(null);

    try {
      const result = await enrollBiometrics(label.trim() || 'Este dispositivo');
      setMessage({ tone: 'ok', text: `Listo. «${result.label}» ya puede entrar sin contraseña.` });
      await queryClient.invalidateQueries({ queryKey: ['webauthn', 'credentials'] });
    } catch (error) {
      setMessage({ tone: 'bad', text: describeBiometricError(error) });
    } finally {
      setEnrolling(false);
    }
  };

  const remove = useMutation({
    mutationFn: deleteWebAuthnCredential,
    onSuccess: async () => {
      setMessage({ tone: 'ok', text: 'Se quitó ese dispositivo.' });
      await queryClient.invalidateQueries({ queryKey: ['webauthn', 'credentials'] });
    },
    onError: () => setMessage({ tone: 'bad', text: 'No se pudo quitar. Inténtalo otra vez.' }),
  });

  const list = credentials.data?.credentials ?? [];

  return (
    <div className="space-y-4">
      {/* Lo que hay vinculado ahora mismo. */}
      {list.length > 0 && (
        <ul className="space-y-2">
          {list.map((credential) => (
            <li
              key={credential.id}
              className="flex items-center gap-3 rounded-ed border border-ed-bg-border/40 bg-ed-bg-elevated/30 p-3"
            >
              <Smartphone size={18} className="shrink-0 text-ed-success" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{credential.label}</p>
                <p className="text-xs text-ed-text-muted">
                  {credential.lastUsedAt
                    ? `Se usó por última vez el ${new Date(credential.lastUsedAt).toLocaleDateString('es-CO')}`
                    : 'Todavía no se ha usado para entrar'}
                  {credential.backedUp && ' · guardado en la nube de tu teléfono'}
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 text-ed-text-muted hover:text-ed-coral-light"
                disabled={remove.isPending}
                aria-label={`Quitar ${credential.label}`}
                onClick={() => {
                  if (
                    window.confirm(
                      `¿Quitar «${credential.label}»? Podrás seguir entrando con tu contraseña.`,
                    )
                  ) {
                    remove.mutate(credential.id);
                  }
                }}
              >
                <Trash2 size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Vincular uno nuevo. */}
      {supported === false && (
        <p className="text-sm text-ed-text-muted">
          Este dispositivo no tiene huella ni reconocimiento de cara configurado, así que
          la opción no está disponible aquí. Puedes seguir entrando con tu contraseña, y
          activarlo desde un teléfono.
        </p>
      )}

      {supported === true && (
        <div className="space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">¿Cómo llamas a este dispositivo?</span>
            <input
              className="ed-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Mi teléfono"
              maxLength={120}
              autoComplete="off"
            />
            <span className="mt-1 block text-xs text-ed-text-muted">
              Es solo para que lo reconozcas en la lista de arriba.
            </span>
          </label>

          <button
            type="button"
            className="ed-btn-primary w-full"
            disabled={enrolling}
            onClick={() => void enroll()}
          >
            <Fingerprint size={18} aria-hidden="true" />
            {enrolling ? 'Esperando tu huella…' : 'Vincular la huella de este dispositivo'}
          </button>
        </div>
      )}

      {supported === null && (
        <p className="text-sm text-ed-text-muted">Comprobando qué puede hacer este dispositivo…</p>
      )}

      {message && (
        <p
          role="status"
          className={`rounded-ed px-3 py-2 text-sm ${
            message.tone === 'ok'
              ? 'border border-ed-success/40 bg-ed-success/10 text-ed-success'
              : 'border border-ed-coral-deep/40 bg-ed-coral-deep/10 text-ed-coral-light'
          }`}
        >
          {message.text}
        </p>
      )}

      {list.length > 0 && (
        <p className="flex items-start gap-2 text-xs text-ed-success">
          <ShieldCheck size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            En el servidor solo se guarda la clave <strong>pública</strong>. Aunque alguien
            se llevara esa tabla entera no podría entrar en ninguna cuenta: le faltaría la
            clave privada, que vive en tu dispositivo y no sale de él.
          </span>
        </p>
      )}

      <Note>
        Esto <strong>no sustituye a tu contraseña</strong>: es una forma más cómoda de
        entrar en este dispositivo. Tu contraseña sigue siendo la que manda, y si pierdes
        el teléfono entras con ella y quitas el dispositivo de la lista de arriba.
      </Note>
    </div>
  );
}
